import type {
  MCPServerConfigurationType,
  MCPToolConfigurationType,
} from "@app/lib/actions/mcp";
import { MCPServerConfigurationSchema } from "@app/lib/actions/mcp_schemas";
import type { AgentMCPActionWithOutputType } from "@app/types/actions";
import type {
  AgentMessageType,
  CreditSpendCheckpointStatus,
  InlineActivityStep,
} from "@app/types/assistant/conversation";
import type { MODEL_PROVIDER_IDS } from "@app/types/assistant/models/providers";
import { ReasoningEffortSchema } from "@app/types/assistant/models/reasoning";
import type { ModelIdType } from "@app/types/assistant/models/types";
import type { AgentSkillType } from "@app/types/assistant/skill_configuration";
import { DbModelIdSchema } from "@app/types/shared/model_id";
import type { TagType } from "@app/types/tag";
import { TagSchema } from "@app/types/tag";
import { UserSchema } from "@app/types/user";
import { z } from "zod";

/**
 * Agent configuration
 */

export const GLOBAL_AGENT_STATUSES = [
  "active",
  "disabled_by_admin",
  "disabled_missing_datasource",
  "disabled_free_workspace",
] as const;
export type GlobalAgentStatus = (typeof GLOBAL_AGENT_STATUSES)[number];

/**
 * Agent statuses:
 * - "active" means the agent can be used directly
 * - "archived" means the agent was either deleted, or that there is a newer
 *   version
 * - "draft" is used for the "try" button in builder, when the agent is not yet
 *   fully created / updated
 * - "pending" is used when the agent builder is opened for a new agent, before
 *   it is saved for the first time (allows capturing sId early). It allows having
 *   a sId before creating the agent.
 */
export const AGENT_STATUSES = [
  "active",
  "archived",
  "draft",
  "pending",
] as const;
export type AgentStatus = (typeof AGENT_STATUSES)[number];

export function isAgentStatus(status: string): status is AgentStatus {
  return AGENT_STATUSES.some((agentStatus) => agentStatus === status);
}

const AGENT_CONFIGURATION_STATUSES = [
  "active",
  "archived",
  "draft",
  "pending",
  "disabled_by_admin",
  "disabled_missing_datasource",
  "disabled_free_workspace",
] as const;
export const AgentConfigurationStatusSchema = z.enum(
  AGENT_CONFIGURATION_STATUSES
);
export type AgentConfigurationStatus = z.infer<
  typeof AgentConfigurationStatusSchema
>;

/**
 * Agent configuration scope
 * - 'global' scope are Azimut agents, not editable, inside-list for all, cannot be overriden
 * - 'visible' scope are published agents
 * - 'hidden' scope are unpuiblished agents, visible by editors only
 */
export const AGENT_CONFIGURATION_SCOPES = [
  "global",
  "visible",
  "hidden",
] as const;
export type AgentConfigurationScope =
  (typeof AGENT_CONFIGURATION_SCOPES)[number];

// Agent names are copied onto audit-log targets. Cap them so a create/update
// cannot skip audit emission by overflowing the WorkOS payload limit.
export const AGENT_NAME_MAX_LENGTH = 512;

/**
 * Defines strategies for fetching agent configurations based on various
 * 'views':
 * - 'current_user': Retrieves agents created or edited by the current user.
 * - 'list': Retrieves all active agents accessible to the user
 * - 'all': All non-private agents (so combines workspace, published and global
 *   agents); used e.g. for non-user calls such as API
 * - 'published': Retrieves all published agents.
 * - 'global': Retrieves all agents exclusively with a 'global' scope.
 * - 'analytics': Agents the caller may report on, for the workspace analytics
 *   surfaces. Managers and admins get every active agent in the workspace —
 *   whatever its scope, whoever edits it, and whether or not they can read the
 *   spaces it is built on — since analytics reports on all of them. Everyone
 *   else gets the same set as 'all'. Never fails on permissions: the scope
 *   opens up with the caller's role.
 * - 'admin_internal': Grants access to all agents, including private ones.
 * - 'manage': Retrieves all agents for the manage agents view (same as list, but including disabled agents).
 * - 'manage_unrestricted': Same as 'manage', but without the visibility
 *   restrictions: every active agent of the workspace, whatever its scope,
 *   whoever edits it, and whether or not the caller can read the spaces it is
 *   built on. Admins only.
 * - 'archived': Retrieves all agents that are archived. Only available to super
 *   users. Intended strictly for internal use with necessary superuser or admin
 *   authorization.
 * - 'favorites': Retrieves all agents marked as favorites by the current user.
 */
// TODO(agent-discovery) remove workspace, published, global
export type AgentsGetViewType =
  | "current_user"
  | "list"
  | "all"
  | "analytics"
  | "manage_unrestricted"
  | "published"
  | "global"
  | "admin_internal"
  | "manage"
  | "archived"
  | "favorites";

export type AgentRecentAuthors = readonly string[];

export const AGENT_REINFORCEMENT_MODES = ["auto", "on", "off"] as const;
export type AgentReinforcementMode = (typeof AGENT_REINFORCEMENT_MODES)[number];

export const AgentUsageSchema = z.object({
  messageCount: z.number(),
  conversationCount: z.number(),
  userCount: z.number(),
  timePeriodSec: z.number(),
});

export type AgentUsageType = z.infer<typeof AgentUsageSchema>;

// ModelProviderIdSchema and ModelIdSchema are inlined here to avoid importing
// from models/models.ts and models/providers.ts which have io-ts side effects
// (ioTsEnum → uuid) that crash in the Temporal workflow sandbox.
export const AgentModelConfigurationSchema = z.object({
  providerId: z.custom<(typeof MODEL_PROVIDER_IDS)[number]>(
    (val) => typeof val === "string"
  ),
  modelId: z.custom<ModelIdType>((val) => typeof val === "string"),
  temperature: z.number(),
  reasoningEffort: ReasoningEffortSchema.optional(),
  responseFormat: z.string().optional(),
  metaData: z.record(z.string(), z.unknown()).optional(),
});

export type AgentModelConfigurationType = z.infer<
  typeof AgentModelConfigurationSchema
>;

export type AgentFetchVariant = "light" | "full" | "extra_light";

export type GlobalAgentContext = {
  userMessageRank: number;
  sidekickIsNewAgentFromScratch?: boolean;
  // Pre-formatted text that the Azimut global agent should echo as its NOOP
  // static reply for this turn. Set by `getStaticReplyForUserMessage` when the
  // triggering user message is a system-posted bootstrap/status update that
  // carries its own rendered content.
  staticReply?: string;
};

// The synchronously-available part of a configuration: the static row of an agent plus the caller's
// in-memory permission snapshot (`canRead`/`canEdit` need no query). This is exactly what
// `AgentResource.toJSON` renders; the `*EnrichmentSchema`s below add the fields that each need a
// query, and `Light`/`AgentConfigurationSchema` are recomposed from base + enrichments.
// `agentModelId` carries the agent's stable identity (the `AgentModel.id`) onto every rendered
// configuration so callers can correlate a configuration back to its `agents` row.
/**
 * @cc [owner:tdraier,label:backend] agent-model-id-population
 * `agentModelId` MUST be the stable `AgentModel.id` for custom agents (`scope !== "global"`) and
 * MUST be `null` for global agents (`scope === "global"`). A custom-agent configuration MUST NOT
 * carry a `null` `agentModelId`.
 */
export const AgentConfigurationBaseSchema = z.object({
  id: DbModelIdSchema,
  // Stable `AgentModel.id` shared across every version. Null for global agents,
  // which have no `agent` identity row.
  agentModelId: DbModelIdSchema.nullable(),
  versionCreatedAt: z.string().nullable(),
  sId: z.string(),
  version: z.number(),
  versionAuthorId: DbModelIdSchema.nullable(),
  model: AgentModelConfigurationSchema,
  status: AgentConfigurationStatusSchema,
  scope: z.enum(AGENT_CONFIGURATION_SCOPES),
  name: z.string(),
  description: z.string(),
  pictureUrl: z.string(),
  maxStepsPerRun: z.number(),
  templateId: z.string().nullable(),
  visualizationEnabled: z.boolean().optional(),
  requestedGroupIds: z.array(z.array(z.string())),
  requestedSpaceIds: z.array(z.string()),
  reinforcement: z.enum(AGENT_REINFORCEMENT_MODES).optional(),
  lastReinforcementAnalysisAt: z.string().nullable().optional(),
  ignoreCreditSpendThresholdAlert: z.boolean().optional(),
  canRead: z.boolean(),
  canEdit: z.boolean(),
  // Whether the caller can view the agent's private fields (instructions, skills, tools); they are
  // redacted when `false` (see `agent-content-visibility`).
  canViewContent: z.boolean().optional(),
  omittedThinking: z.boolean().optional(),
});

export type AgentConfigurationBaseType = z.infer<
  typeof AgentConfigurationBaseSchema
>;

// -- Builder enrichments: fields the resource builders add on top of the base via a matching
// `enrichWith*` step in `lib/resources/agent_resource_serialization.ts`. Each schema's inferred type
// is the value of the `Map` that step returns, handed to the configuration builders there. --

// `enrichWithFavorites` (per requesting user).
export const AgentFavoriteEnrichmentSchema = z.object({
  userFavorite: z.boolean(),
});
export type AgentFavoriteEnrichment = z.infer<
  typeof AgentFavoriteEnrichmentSchema
>;

// `enrichWithTags`.
export const AgentTagsEnrichmentSchema = z.object({
  tags: z.array(TagSchema),
});
export type AgentTagsEnrichment = z.infer<typeof AgentTagsEnrichmentSchema>;

// `enrichWithInstructions` (both `null` when the caller cannot view the content). `instructionsHtml`
// is only part of the full configuration shape.
export const AgentInstructionsEnrichmentSchema = z.object({
  instructions: z.string().nullable(),
  instructionsHtml: z.string().nullable(),
});
export type AgentInstructionsEnrichment = z.infer<
  typeof AgentInstructionsEnrichmentSchema
>;

// `enrichWithActions` (full only).
export const AgentActionsEnrichmentSchema = z.object({
  actions: z.array(MCPServerConfigurationSchema),
});
export type AgentActionsEnrichment = z.infer<
  typeof AgentActionsEnrichmentSchema
>;

// -- Route decorations: optional fields the list/detail HTTP handlers attach behind their `with*`
// query flags (getAgentsUsage / feedback counts / getAgentsEditors / getAgentsRecentAuthors). The
// resource builders do NOT produce them: they need request context the builders don't have — a Redis
// handle and a page `limit` (usage), a feedback time window (feedbacks), per-user "Me" rendering
// (authors). They live in the wire type only because a route may add them. --
export const AgentConfigurationDecorationsSchema = z.object({
  usage: AgentUsageSchema.optional(),
  feedbacks: z.object({ up: z.number(), down: z.number() }).optional(),
  editors: z.array(UserSchema).optional(),
  lastAuthors: z.array(z.string()).readonly().optional(),
});

// The full-only delta over light: `instructionsHtml` + `actions` + `codeDefinedSkillIds`.
export const AgentConfigurationFullFieldsSchema =
  AgentActionsEnrichmentSchema.merge(
    AgentInstructionsEnrichmentSchema.pick({ instructionsHtml: true })
  ).extend({
    // Code-defined skill ids, only set by the in-code global agent definitions.
    codeDefinedSkillIds: z.array(z.string()).optional(),
  });

// Light = base + the builder enrichments the light builder produces (instructions, favorite, tags) +
// the optional route decorations.
export const LightAgentConfigurationSchema = AgentConfigurationBaseSchema.merge(
  AgentInstructionsEnrichmentSchema.pick({ instructions: true })
)
  .merge(AgentFavoriteEnrichmentSchema)
  .merge(AgentTagsEnrichmentSchema)
  .merge(AgentConfigurationDecorationsSchema);

/**
 * @swaggerschema AgentConfiguration (swagger_schemas.ts), PrivateLightAgentConfiguration (swagger_private_schemas.ts)
 */
export type LightAgentConfigurationType = z.infer<
  typeof LightAgentConfigurationSchema
>;

// Full = light + the full-only fields.
export const AgentConfigurationSchema = LightAgentConfigurationSchema.merge(
  AgentConfigurationFullFieldsSchema
);

export type AgentConfigurationType = z.infer<typeof AgentConfigurationSchema>;

/**
 * An agent configuration with its attached skills resolved, which is what the API serializes.
 * `codeDefinedSkillIds` is stripped on the way out: `skills` supersedes it, and the ids on their
 * own are an internal detail of how global agents declare theirs.
 */
export type AgentConfigurationWithSkillsType = LightAgentConfigurationType & {
  skills: AgentSkillType[];
};

export type AgentConfigurationWithoutModelType = Omit<
  AgentConfigurationType,
  "model"
>;

export type LightAgentConfigurationWithoutModelType = Omit<
  LightAgentConfigurationType,
  "model"
>;

export interface TemplateAgentConfigurationType {
  name: string;
  pictureUrl: string;

  scope: AgentConfigurationScope;
  description: string;
  model: AgentModelConfigurationType;
  actions: MCPServerConfigurationType[];
  instructions: string | null;
  isTemplate: true;
  maxStepsPerRun?: number;
  tags: TagType[];
}

export const MAX_STEPS_USE_PER_RUN_LIMIT = 64;
const ACTIONS_PER_STEP_BY_DEPTH = [8, 8, 4, 2] as const;
const MAX_DEPTH_WITH_ACTION_LIMIT = ACTIONS_PER_STEP_BY_DEPTH.length - 1;

// Returns the max actions per step for a given conversation depth.
// Keeps max actions for the first 2 depth levels, then halves: 8 → 8 → 4 → 2,
// capping total concurrent agent loop activities at 512 for a single user message.
export function getMaxActionsPerStep(depth: number): number {
  const normalizedDepth = Number.isFinite(depth) ? Math.trunc(depth) : 0;
  const boundedDepth = Math.max(
    0,
    Math.min(normalizedDepth, MAX_DEPTH_WITH_ACTION_LIMIT)
  );

  return ACTIONS_PER_STEP_BY_DEPTH[boundedDepth];
}

/**
 * Agent events
 */

export const AgentErrorCategories = [
  "retryable_model_error",
  "context_window_exceeded",
  "empty_content",
  "provider_internal_error",
  "stream_error",
  "unknown_error",
  "invalid_response_format_configuration",
  "credits_exhausted",
] as const;

export type AgentErrorCategory = (typeof AgentErrorCategories)[number];

export function isAgentErrorCategory(
  category: unknown
): category is AgentErrorCategory {
  return AgentErrorCategories.includes(category as AgentErrorCategory);
}

// Generic type for the content of an agent / tool error.
export type GenericErrorContent = {
  code: string;
  message: string;
  metadata: Record<string, string | number | boolean> | null;
};

// Generic event sent when an error occurred during the model call.
export type AgentErrorEvent = {
  type: "agent_error";
  created: number;
  configurationId: string;
  messageId: string;
  error: GenericErrorContent;
  runIds?: string[];
};

// Generic event sent when an agent message is done (could be successful, failed, or cancelled).
export type AgentMessageDoneEvent = {
  type: "agent_message_done";
  created: number;
  conversationId: string;
  configurationId: string;
  messageId: string;
  status: "success" | "error";
};

// Event sent once the persisted credit attribution is available to read.
export type AgentMessageConsumptionUpdatedEvent = {
  type: "agent_message_consumption_updated";
  conversationId: string;
  costCredits: number | null;
  created: number;
  messageId: string;
};

// Event sent when an error occurred during the tool call.
export type ToolErrorEvent = {
  type: "tool_error";
  created: number;
  configurationId: string;
  messageId: string;
  conversationId: string;
  error: GenericErrorContent;
  isLastBlockingEventForStep: boolean;
  // TODO(DURABLE-AGENTS 2025-08-25): Move to a deferred event base interface.
  metadata?: {
    pubsubMessageId?: string;
  };
};

export type AgentToolCallStartedEvent = {
  type: "tool_call_started";
  created: number;
  configurationId: string;
  messageId: string;
  toolCallId?: string;
  toolCallIndex?: number;
  toolName: string;
};

// Event sent once the action is completed, we're moving to generating a message if applicable.
export type AgentActionSuccessEvent = {
  type: "agent_action_success";
  created: number;
  configurationId: string;
  messageId: string;
  action: AgentMCPActionWithOutputType;
};

// Event sent to stop the generation.
export type AgentGenerationCancelledEvent = {
  type: "agent_generation_cancelled";
  created: number;
  configurationId: string;
  messageId: string;
  status: "cancelled" | "interrupted";
};

// Server-rendered view of a finished agent message (the displayed body, chain of
// thought, and activity steps). Computed from the persisted step contents (the
// same source reload uses), so the client can trust it wholesale at stream end
// instead of reconciling its incrementally-built view.
export type AgentMessageContentView = {
  content: string | null;
  chainOfThought: string | null;
  activitySteps: InlineActivityStep[];
};

// Event sent when the agent loop was gracefully stopped (current step completed, then exited).
export type AgentMessageGracefullyStoppedEvent = {
  type: "agent_message_gracefully_stopped";
  created: number;
  configurationId: string;
  messageId: string;
  message: AgentMessageType;
  // Optional: absent on events from an older server during a deploy window.
  contentView?: AgentMessageContentView;
  runIds: string[];
};

// Event sent once the message is completed and successful.
export type AgentMessageSuccessEvent = {
  type: "agent_message_success";
  created: number;
  configurationId: string;
  messageId: string;
  message: AgentMessageType;
  // Optional: absent on events from an older server during a deploy window.
  contentView?: AgentMessageContentView;
  runIds: string[];
};

export type AgentActionsEvent = {
  type: "agent_actions";
  created: number;
  runId: string;
  actions: Array<{
    action: MCPToolConfigurationType;
    functionCallId: string;
  }>;
};

export type AgentContextPrunedEvent = {
  type: "agent_context_pruned";
  created: number;
  configurationId: string;
  messageId: string;
};

// Emitted when the loop pauses on a credit spend checkpoint, and again once a decision has been
// recorded.
export type AgentCreditSpendCheckpointUpdatedEvent = {
  type: "agent_credit_spend_checkpoint_updated";
  created: number;
  configurationId: string;
  messageId: string;
  status: CreditSpendCheckpointStatus;
};
